import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/aix/Nav";
import { Footer } from "@/components/aix/Sections2";
import { getArticle, formatDate } from "@/data/articles";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    document.title = article
      ? `${article.title} — AI Advantage Exchange`
      : "AI Advantage Exchange";
    window.scrollTo(0, 0);
    return () => {
      document.title = "AI Advantage Exchange — Curated AI Professional Community";
    };
  }, [article]);

  if (!article) {
    return (
      <div className="bg-white text-slate-900 min-h-screen antialiased" data-testid="article-not-found">
        <Nav />
        <main className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-4">Article not found</h1>
          <Link to="/blog" className="text-aix-cyandeep font-semibold hover:underline" data-testid="article-back-link">
            ← Back to the Dispatch
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-900 min-h-screen antialiased" data-testid="article-page">
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-aix-navy mb-10"
          data-testid="article-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> The Dispatch
        </Link>

        <div className="flex items-center gap-3 font-mono-lbl text-[11px] tracking-widest uppercase mb-5">
          <span className="text-aix-cyandeep bg-sky-50 border border-aix-cyan/30 px-2.5 py-1 rounded">
            {article.category}
          </span>
          <span className="text-slate-400">
            {formatDate(article.date)} · {article.readTime} · {article.author}
          </span>
        </div>

        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.1] text-slate-900 mb-10" data-testid="article-title">
          {article.title}
        </h1>

        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm mb-12 h-56 md:h-80">
          <img
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
            data-testid="article-image"
          />
        </div>

        <div data-testid="article-body">
          {article.content.map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2 key={i} className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">
                  {block.text}
                </h2>
              );
            }
            if (block.type === "ul") {
              return (
                <ul key={i} className="space-y-3 my-6">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-600 text-base leading-relaxed">
                      <span className="mt-2.5 w-2 h-2 bg-aix-cyan shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-slate-600 text-base md:text-lg leading-relaxed mb-6">
                {block.text}
              </p>
            );
          })}
        </div>

        <div className="mt-16 border border-slate-200 bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="article-cta">
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900">Talk about this with the people building it</h3>
            <p className="text-slate-500 text-sm mt-2 max-w-md">
              AIX members tear down real AI systems like the ones in this article every month. Join the waitlist.
            </p>
          </div>
          <a
            href="/#apply"
            className="inline-flex items-center gap-2 bg-aix-navy hover:bg-[#16407F] text-white text-sm font-semibold px-6 py-3.5 rounded transition-colors whitespace-nowrap"
            data-testid="article-apply-cta"
          >
            Join Waitlist <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
